// Prosjektstyring i dugnad-modulen: selvregistrering, sprints og kanban.
//
// ── KANBAN: HVORFOR ET OVERLEGG, OG IKKE EN NY TAVLE ───────────────────────
// VALE har allerede en kanban: `vale/data/kanban.json` med 280 kort, kolonnene
// backlog/next/doing/done/wontdo og prosjektene GNÅ/SPARK/SKADE/HILDR/LODUR/VALE.
// Fila er GENERERT fra `@skade/contracts/capabilities.ts` — den sier det selv i
// `generated`-feltet. Å la et web-UI skrive rett i den ville blitt overskrevet
// ved neste regenerering, og da forsvinner arbeidet til den som flyttet kortet.
//
// Løsningen er et OVERLEGG: genererte kort leses fra VALE, mens endringer
// (kolonneflytting, tilordning, sprint-kobling) og NYE kort som ikke kommer fra
// capabilities.ts lever i modulens eget lager. De slås sammen ved lesing.
// Dermed er VALE fortsatt sannheten om hva som er BYGGET, og modulen er sannheten
// om hva som er I ARBEID — uten at de to kan ødelegge for hverandre.
import { z } from 'zod';
// ── Selvregistrering ────────────────────────────────────────────────────────
/**
 * Registreringslenke. En engangs-invitasjon som lar en person registrere seg
 * selv med ansattnummer og navn, og få pseudonym + token tilbake.
 *
 * Hvorfor engangs og med utløp: lenken gir rett til å opprette en konto som kan
 * føre timer. En lenke som ligger evig i en Signal-tråd er en åpen dør.
 */
export const RegistreringslenkeSchema = z.object({
    /** Ugjennomtrengelig token i URL-en. Lagres HASHET, som API-tokens. */
    id: z.string().min(16),
    /** Hvem som opprettet lenken (pseudonym). */
    opprettetAv: z.string().min(1),
    opprettetTsMs: z.number().int(),
    /** Utløper — standard 7 døgn. Ingen evige lenker. */
    utloperTsMs: z.number().int(),
    /** Antall ganger den kan brukes. 1 = personlig invitasjon. */
    maksBruk: z.number().int().min(1).max(200).default(1),
    brukt: z.number().int().min(0).default(0),
    /** Forhåndssatt tilhørighet, så den enkelte ikke må skrive den selv. */
    tilhorighet: z.string().max(120).optional(),
    /** Kan lenken deles ut til flere (område-registrering) eller er den personlig? */
    merknad: z.string().max(200).optional(),
});
/** Er lenken brukbar nå? Utløp og bruksantall er de to reelle feilkildene. */
export function kontrollerLenke(l, naa) {
    if (naa > l.utloperTsMs)
        return { gyldig: false, grunn: 'registreringslenken er utløpt' };
    if (l.brukt >= l.maksBruk) {
        return { gyldig: false, grunn: 'registreringslenken er allerede brukt opp' };
    }
    return { gyldig: true };
}
/** Full URL til registreringssiden. Basis settes av drift, ikke hardkodet. */
export function registreringsUrl(basis, lenkeId) {
    const b = basis.replace(/\/+$/, '');
    return `${b}/registrer?kode=${encodeURIComponent(lenkeId)}`;
}
/** Standard levetid for en registreringslenke. */
export const LENKE_LEVETID_MS = 7 * 24 * 60 * 60 * 1000;
// ── Sprints ─────────────────────────────────────────────────────────────────
export const SprintSchema = z.object({
    id: z.string().min(1),
    navn: z.string().min(1).max(120),
    /** Mål for sprinten — hva skal være annerledes når den er ferdig. */
    mal: z.string().max(1000).default(''),
    startTsMs: z.number().int(),
    sluttTsMs: z.number().int(),
    /** Kort-id-er som er tatt inn i sprinten (både CAP- og egne kort). */
    kort: z.array(z.string().min(1)).max(500).default([]),
    status: z.enum(['planlagt', 'aktiv', 'ferdig', 'avbrutt']).default('planlagt'),
    opprettetAv: z.string().min(1),
    opprettetTsMs: z.number().int(),
});
/**
 * Status for en sprint. Merk at `tidsandel` bare måler TID, ikke fremdrift —
 * å blande de to er den klassiske feilen i sprint-visninger. Fremdrift må komme
 * fra kortenes kolonner (se `sprintFremdrift`).
 */
export function sprintStatus(s, naa) {
    const dag = 24 * 60 * 60 * 1000;
    const total = Math.max(1, s.sluttTsMs - s.startTsMs);
    const gaatt = Math.min(total, Math.max(0, naa - s.startTsMs));
    return {
        sprint: s,
        dagerTotalt: Math.ceil(total / dag),
        dagerIgjen: Math.max(0, Math.ceil((s.sluttTsMs - naa) / dag)),
        tidsandel: gaatt / total,
        antallKort: s.kort.length,
    };
}
// ── Kanban ──────────────────────────────────────────────────────────────────
export const KANBAN_KOLONNER = ['backlog', 'next', 'doing', 'done', 'wontdo'];
export const KOLONNE_NAVN = {
    backlog: 'Backlog (planlagt)',
    next: 'Neste (prioritert)',
    doing: 'Pågår',
    done: 'Ferdig',
    wontdo: 'Utgår',
};
/** Et kort slik VALE genererer det. Skal leses, ikke skrives. */
export const GenerertKortSchema = z.object({
    id: z.string().min(1),
    project: z.string().min(1),
    category: z.string().default(''),
    title: z.string().min(1),
    note: z.string().default(''),
    column: z.string().min(1),
});
/**
 * Overlegg over ett kort. Bare felt som er SATT overstyrer det genererte —
 * derfor er alt valgfritt. Slik kan VALE endre tittel og notat uten at
 * overlegget fryser gammel tekst.
 */
export const KortOverleggSchema = z.object({
    kortId: z.string().min(1),
    kolonne: z.enum(KANBAN_KOLONNER).optional(),
    /** Tilordnet person (pseudonym). */
    tilordnet: z.string().optional(),
    sprintId: z.string().optional(),
    /** Fri rekkefølge innen kolonnen (lavere = høyere opp). */
    rekkefolge: z.number().optional(),
    endretAv: z.string().min(1),
    endretTsMs: z.number().int(),
});
/** Kort som er opprettet i modulen, ikke generert av VALE. */
export const EgetKortSchema = z.object({
    id: z.string().min(1),
    project: z.string().min(1),
    category: z.string().max(120).default(''),
    title: z.string().min(1).max(200),
    note: z.string().max(2000).default(''),
    kolonne: z.enum(KANBAN_KOLONNER).default('backlog'),
    tilordnet: z.string().optional(),
    sprintId: z.string().optional(),
    rekkefolge: z.number().optional(),
    opprettetAv: z.string().min(1),
    opprettetTsMs: z.number().int(),
    endretTsMs: z.number().int().optional(),
});
function somKolonne(v) {
    return KANBAN_KOLONNER.includes(v) ? v : 'backlog';
}
/**
 * Slå sammen VALEs genererte kort med modulens overlegg og egne kort.
 *
 * Rekkefølgen er viktig: generert kort → overlegg på toppen. Et overlegg for et
 * kort som ikke lenger finnes i VALE (kapabiliteten er fjernet) forkastes, men
 * det skal ikke skape et tomt kort — det ville vist arbeid som ikke finnes.
 */
export function slaaSammenKanban(generert, overlegg, egne) {
    const perId = new Map(overlegg.map((o) => [o.kortId, o]));
    const ut = [];
    for (const g of generert) {
        const o = perId.get(g.id);
        ut.push({
            id: g.id,
            project: g.project,
            category: g.category,
            title: g.title,
            note: g.note,
            kolonne: o?.kolonne ?? somKolonne(g.column),
            tilordnet: o?.tilordnet,
            sprintId: o?.sprintId,
            rekkefolge: o?.rekkefolge,
            kilde: 'vale',
            overstyrt: !!o?.kolonne && o.kolonne !== somKolonne(g.column),
        });
    }
    for (const e of egne) {
        ut.push({
            id: e.id, project: e.project, category: e.category, title: e.title, note: e.note,
            kolonne: e.kolonne, tilordnet: e.tilordnet, sprintId: e.sprintId,
            rekkefolge: e.rekkefolge, kilde: 'modul', overstyrt: false,
        });
    }
    return ut;
}
/** Kort gruppert per kolonne, sortert på rekkefølge og deretter id (stabilt). */
export function kanbanPerKolonne(kort) {
    const ut = Object.fromEntries(KANBAN_KOLONNER.map((k) => [k, []]));
    for (const k of kort)
        ut[k.kolonne].push(k);
    for (const k of KANBAN_KOLONNER) {
        ut[k].sort((a, b) => (a.rekkefolge ?? 1e9) - (b.rekkefolge ?? 1e9) || a.id.localeCompare(b.id, 'nb'));
    }
    return ut;
}
/** Fremdrift i et kortsett. «wontdo» holdes utenfor — utgåtte kort er ikke arbeid. */
export function fremdrift(kort) {
    const med = kort.filter((k) => k.kolonne !== 'wontdo');
    const ferdig = med.filter((k) => k.kolonne === 'done').length;
    return {
        totalt: med.length,
        ferdig,
        paagaar: med.filter((k) => k.kolonne === 'doing').length,
        andel: med.length ? ferdig / med.length : 0,
    };
}
/** Fremdrift for kortene i én sprint. */
export function sprintFremdrift(s, kort) {
    const sett = new Set(s.kort);
    return fremdrift(kort.filter((k) => sett.has(k.id) || k.sprintId === s.id));
}
//# sourceMappingURL=prosjekt.js.map